import React, { useState, useEffect  } from "react";
import { useLocation } from 'react-router-dom';
import axios from "axios";
import html2pdf from "html2pdf.js";
import emailjs from 'emailjs-com';

import SignatureCanvas from "react-signature-canvas";
import './BillingReport.css';
import './index.css';
import config from '../../config/config';

const BillingReportGenerator = () => {
  const [patientId, setPatientId] = useState("");    
  const location = useLocation();
  const { patientName = '' } = location.state?.state || {}; // Extract patientName from navigation state
  const [patientData, setPatientData] = useState(null);
  const [cptCode, setCptCode] = useState("");
  const [reportData, setReportData] = useState(null);
  const [email, setEmail] = useState("");
  const [apiEndpoint, setApiEndpoint] = useState("");
  const [error, setError] = useState(null);
  const [signature, setSignature] = useState(null);
  const backendURL = `${config.backendServerHost}`;
  const [file, setFile] = useState(null); // Example useState hook
  const [emailData, setEmailData] = useState({
        name: '',
        email: '',
        message: '',
        pdfFile: null,
    });

// Place console.log statements to debug received state
console.log('Received state:', location.state);
console.log('Extracted Patient Name:', patientName);

    useEffect(() => {
        if (!patientName) {
            setError('No patient name provided');
            return;
        }

     // Fetch all patients and find the one matching patientName
        fetch(`${backendURL}/patients/api/patients`)
            .then((res) => {
                if (!res.ok) {
                    throw new Error('Failed to fetch patient data');
                }
                return res.json();
            })
            .then((data) => {
                const patient = data.find((p) => p.name === patientName); // Find patient by name
                if (patient) {
                // Return both patientName and patientId
                setPatientData({
                    patientName: patient.name,
                    patientId: patient.id,
                });
                } else {
                    setError(`Patient with name "${patientName}" not found`);
                }
            })
            .catch((err) => {
                setError(`Error fetching patients: ${err.message}`);
            });
    }, [patientName]);

    if (error) {
        return <div>Error: {error}</div>;
    }

    if (!patientData) {
        return <div>Loading patient data...</div>;
    }

  const fetchReport = async () => {
    if (!patientData.patientId || !cptCode) {
      setError("Please select a CPT Code.");
      return;
    }
    try {
      const response = await axios.get(
        `${backendURL}/patients/api/billingreportgeneration/${patientData.patientId}`,
        { params: { cptCode } }
      );
      setReportData(response.data);
      setError("");
    } catch (err) {
      setError("Error fetching report data.");
      console.error(err);
    }
  };

  const generatePDF = () => {
    if (!reportData) return;

    const title =
      cptCode === "95251"
        ? "Remote Patient Monitoring Report for CGM Data (CPT 95251)"
        : "Remote Patient Monitoring Report for CGM Data (CPT 99457)";

    const element = document.getElementById("report-content");
    const opt = {
      margin: 1,
      filename: `${title.replace(/\s+/g, "_")}_${reportData.name}.pdf`,
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    };
    html2pdf().set(opt).from(element).save();
  };



// functions to send email from react

    const handleChange = (e) => {
        const { name, value } = e.target;
        setEmailData({ ...emailData, [name]: value });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        const reader = new FileReader();

        reader.onloadend = () => {
            setEmailData({ ...emailData, pdfFile: reader.result }); // Base64 string of the PDF
        };

        if (file && file.type === 'application/pdf') {
            reader.readAsDataURL(file);
        } else {
            alert('Please select a valid PDF file.');
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        emailjs.send(
            "service_76dp85n", // Replace with your EmailJS Service ID
            "template_xbosxmp", // Replace with your EmailJS Template ID
            {
                from_name: emailData.name,
                to_name: emailData.email,
                message: emailData.message,
                pdf_attachment: emailData.pdfFile, // PDF as Base64 string
            },
            "F542w4aw86N-2ihGW" // Replace with your EmailJS User ID
        )
        .then((response) => {
            console.log('SUCCESS!', response.status, response.text);
            alert('Email sent successfully!');
        })
        .catch((err) => {
            console.error('FAILED...', err);
            alert('Failed to send email.');
        });
      }


  const uploadReport = async () => {
    if (!apiEndpoint || !reportData) {
      setError("Please provide a valid API endpoint.");
      return;
    }
    try {
      const response = await axios.post(apiEndpoint, { report: reportData });
      setError("");
      alert("Report uploaded successfully!");
    } catch (err) {
      setError("Error uploading report.");
      console.error(err);
    }
  };



  return (
    <div className="billing-report-generator">
      <h1>Billing Report Generator</h1>
      <div>
        <label>Patient ID: {patientName}</label>
      </div>
      <div>
        <label>CPT Code:</label>
        <select value={cptCode} onChange={(e) => setCptCode(e.target.value)}>
          <option value="">-- Select CPT Code --</option>
          <option value="95251">95251</option>
          <option value="99457">99457</option>
        </select>
      </div>
      <button onClick={fetchReport}>Generate Report</button>
      {error && <p className="error-message">{error}</p>}
      {reportData && (
        <div>
          <div id="report-content" className="report-content">
            <h2>
              {cptCode === "95251"
                ? "Remote Patient Monitoring Report for CGM Data (CPT 95251)"
                : "Remote Patient Monitoring Report for CGM Data (CPT 99457)"}
            </h2>

            <h3>Summary RPM Interpretation Report</h3>
            <p>
              <strong>Patient Name:</strong> {reportData.name}
            </p>
            <p>
              <strong>Monitoring Period:</strong> {reportData.monitoringPeriod}
            </p>
            <p>
              <strong>Physician:</strong> {reportData.physician}
            </p>
            <p>
              <strong>Avg Glucose:</strong> {reportData.avgGlucose} mg/dL
            </p>
            <p>
              <strong>Time in Range:</strong> {reportData.timeInRange}
            </p>
            <p>
              <strong>Time Below Range:</strong> {reportData.timeBelowRange}
            </p>
            <p>
              <strong>Time Above Range:</strong> {reportData.timeAboveRange}
            </p>
            <p>
              <strong>Hypoglycemia Events:</strong>{" "}
              {reportData.hypoglycemiaEvents}
            </p>
            <p>
              <strong>Hyperglycemia Events:</strong>{" "}
              {reportData.hyperglycemiaEvents}
            </p>
            <p>
              <strong>Review Time:</strong> {reportData.reviewTime} minutes
            </p>
          

<h3>Continuous Glucose Monitoring (CGM) Interpretation Report</h3>
            <p>
              <strong>Date:</strong> {new Date().toLocaleDateString()}
            </p>
            <p>
              <strong>Patient Name:</strong> {reportData.name}
            </p>
            <p>
              <strong>Patient ID:</strong> {reportData.patientId}
            </p>
            <p>
              <strong>Date of Birth:</strong>{" "}
              {new Date(reportData.dob).toLocaleDateString()}
            </p>
            <p>
              <strong>Insurance ID:</strong> {reportData.insuranceID}
            </p>

            <h3>Monitoring Period</h3>
            <p>
              <strong>Start Date:</strong>{" "}
              {reportData.monitoringPeriod.split(" - ")[0]}
            </p>
            <p>
              <strong>End Date:</strong>{" "}
              {reportData.monitoringPeriod.split(" - ")[1]}
            </p>

            <h3>Data Summary</h3>
            <ul>
              <li>
                <strong>Average Glucose Level:</strong>{" "}
                {reportData.avgGlucose} mg/dL
              </li>
              <li>
                <strong>Time in Range (70-180 mg/dL):</strong>{" "}
                {reportData.timeInRange}
              </li>
              <li>
                <strong>Time Below Range (&lt;70 mg/dL):</strong>{" "}
                {reportData.timeBelowRange}
              </li>
              <li>
                <strong>Time Above Range (&gt;180 mg/dL):</strong>{" "}
                {reportData.timeAboveRange}
              </li>
              <li>
                <strong>Number of Hypoglycemia Events:</strong>{" "}
                {reportData.hypoglycemiaEvents}
              </li>
              <li>
                <strong>Number of Hyperglycemia Events:</strong>{" "}
                {reportData.hyperglycemiaEvents}
              </li>
            </ul>

            <h3>Interpretation</h3>
            <ul>
              <li>
                The patient maintained glucose levels within the target range
                for {reportData.timeInRange}, indicating effective glycemic
                control.
              </li>
              <li>
                The patient experienced {reportData.hypoglycemiaEvents}{" "}
                hypoglycemic events, primarily occurring during nighttime hours.
              </li>
              <li>
                There were {reportData.hyperglycemiaEvents} hyperglycemic
                episodes, notably after meals, suggesting potential issues with
                postprandial glucose management.
              </li>
              <li>
                The data suggest that adjustments to mealtime insulin doses and
                potential modifications to dietary habits should be considered.
              </li>
            </ul>

            <h3>Recommendations</h3>
            <ul>
              <li>Adjust the basal insulin dose to address nocturnal hypoglycemia.</li>
              <li>Modify bolus insulin doses or timing to improve postprandial glucose control.</li>
              <li>Schedule follow-up appointment in 30 days to reassess glucose trends.</li>
              <li>
                Provide education on managing hypoglycemic episodes and
                adjusting insulin during physical activity.
              </li>
            </ul>

            <h3>Physician Information</h3>
            <p>
              <strong>Physician Name:</strong> {reportData.physician}
            </p>
            <p>
              <strong>NPI:</strong> {reportData.npi}
            </p>
            <div>
              <label>Physician Signature:</label>
                <SignatureCanvas
                  penColor="black"
                  canvasProps={{ width: 500, height: 200, className: 'sigCanvas' }}
                  ref={(ref) => setSignature(ref)}
                />
              <button onClick={() => console.log(signature.toDataURL())}>
                Save Signature
              </button>
            </div>

            <h3>Billing Details</h3>
            <p>
              <strong>CPT Code:</strong> 95251
            </p>
            <p>
              <strong>Description:</strong> Continuous Glucose Monitoring (CGM)
              professional interpretation of 15+ days of data and report
              generation.
            </p>

          </div>
          

          <button onClick={generatePDF}>Download PDF</button>
          <div>
            <label>Email:</label>
        <form onSubmit={handleSubmit}>
            <input
                type="text"
                name="name"
                placeholder="Your Name"
                onChange={handleChange}
                required
            />
            <input
                type="text"
                name="name"
                placeholder="To Name"
                onChange={handleChange}
                required
            />
            <textarea
                name="message"
                placeholder="Your Message"
                onChange={handleChange}
                required
            ></textarea>
            <input
                type="file"
                name="pdfFile"
                accept="application/pdf"
                onChange={handleFileChange}
                required
            />

            <button type="submit">Send Email</button>
          </form>

          </div>


          <div>
            <label>API Endpoint:</label>
            <input
              type="text"
              value={apiEndpoint}
              onChange={(e) => setApiEndpoint(e.target.value)}
              placeholder="Enter API Endpoint"
            />
            <button onClick={uploadReport}>Upload Report</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingReportGenerator;




